import { ApiScoreCard, ApiScoreCardv2 } from '@/types';
import { ISimulationScore } from '@/types/score';
import mockV2Response from './response_api_scorecards_user_user_id_scorecard.json';

function getRandomDatetimeString() {
  // Generate a random timestamp within a reasonable range (e.g., last 10 years)
  const now = Date.now();
  const threeMonthsAgo = now - 3 * 30 * 24 * 60 * 60 * 1000; // 3 months in milliseconds
  const randomTimestamp = Math.random() * (now - threeMonthsAgo) + threeMonthsAgo;

  // Create a Date object from the random timestamp
  const randomDate = new Date(randomTimestamp);

  // Format the date into an ISO 8601 string (YYYY-MM-DDTHH:mm:ss.sssZ)
  return randomDate.toISOString();
}

/**
 * Mock Simulation Score Responses
 *
 * Comprehensive mocks for different scoring scenarios with proper type separation
 */

// 1. All Numeric
export const mockAllNumericScore: ISimulationScore = {
  uuid: 'mock-all-numeric',
  type: 'numeric',
  score: 3,
  scoreMax: 5,
  description: 'Scoring assessment for conversation 245',
  simulationId: '245',
  dateCompleted: getRandomDatetimeString(),
  certificationId: '1',
  categories: [
    {
      type: 'numeric',
      score: 4,
      scoreMax: 5,
      title: 'Clinical Acumen',
      skillLevel: 'advanced',
      content: [
        {
          title: 'Where You Did Well',
          items: [
            'Excellent clarity when describing the dual-acting mechanism of action of TREMFYA(r).',
            'You capably addressed the differentiation by emphasizing structural inhibition data.',
          ],
        },
        {
          title: 'Area(s) for Development',
          items: [
            'Add more specificity when discussing how structural inhibition data translates into meaningful clinical outcomes.',
          ],
        },
      ],
    },
    {
      type: 'numeric',
      score: 3,
      scoreMax: 5,
      title: 'Selling Skills',
      skillLevel: 'intermediate',
      content: [
        {
          title: 'Where You Did Well',
          items: ["Well done connecting the patient profile to the doctor's practice."],
        },
      ],
    },
    {
      type: 'numeric',
      score: 2,
      scoreMax: 5,
      title: 'Access & Fulfillment',
      skillLevel: 'intermediate',
      content: [
        {
          title: 'Area(s) for Development',
          items: ['Make the connection between access options and specific stakeholder needs.'],
        },
      ],
    },
  ],
};

export const mockAllNumericLowScore: ISimulationScore = {
  uuid: 'mock-all-numeric-low',
  type: 'numeric',
  score: 1,
  scoreMax: 5,
  description: 'Scoring assessment for conversation 245',
  simulationId: '245',
  dateCompleted: getRandomDatetimeString(),
  certificationId: '1',
  categories: [
    {
      type: 'numeric',
      score: 1,
      scoreMax: 5,
      title: 'Clinical Acumen',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Where You Did Well',
          items: [
            'Excellent clarity when describing the dual-acting mechanism of action of TREMFYA(r).',
            'You capably addressed the differentiation by emphasizing structural inhibition data.',
          ],
        },
        {
          title: 'Area(s) for Development',
          items: [
            'Add more specificity when discussing how structural inhibition data translates into meaningful clinical outcomes.',
          ],
        },
      ],
    },
    {
      type: 'numeric',
      score: 2,
      scoreMax: 5,
      title: 'Selling Skills',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Where You Did Well',
          items: ["Well done connecting the patient profile to the doctor's practice."],
        },
      ],
    },
    {
      type: 'numeric',
      score: 2,
      scoreMax: 5,
      title: 'Access & Fulfillment',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Area(s) for Development',
          items: ['Make the connection between access options and specific stakeholder needs.'],
        },
      ],
    },
  ],
};

// 2. All Numeric with 1 Violation
export const mockNumericWithViolationScore: ISimulationScore = {
  ...mockAllNumericScore,
  score: 2,
  dateCompleted: getRandomDatetimeString(),
  uuid: 'mock-numeric-with-violation',
  simulationId: '246',
  categories: [
    ...mockAllNumericScore.categories,
    {
      type: 'violation',
      score: 0,
      scoreMax: 5,
      title: 'Regulatory Compliance',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Compliance Violations',
          items: [
            'Made off-label promotion statements that violate FDA guidelines.',
            'Failed to provide balanced risk-benefit information as required.',
          ],
        },
      ],
    },
  ],
};

// 3. All Pass
export const mockAllPassScore: ISimulationScore = {
  uuid: 'mock-all-pass',
  type: 'numeric',
  score: 5,
  scoreMax: 5,
  pass: true,
  description: 'Certification assessment - all requirements met',
  simulationId: '4',
  dateCompleted: getRandomDatetimeString(),
  certificationId: '1',
  categories: [
    {
      type: 'numeric',
      score: 5,
      scoreMax: 5,
      pass: true,
      title: 'Clinical Knowledge',
      skillLevel: 'expert',
      content: [
        {
          title: 'Requirements Met',
          items: [
            'Successfully demonstrated comprehensive clinical knowledge.',
            'Met all competency requirements for this assessment.',
          ],
        },
      ],
    },
    {
      type: 'numeric',
      score: 4,
      scoreMax: 5,
      pass: true,
      title: 'Communication Skills',
      skillLevel: 'advanced',
      content: [
        {
          title: 'Requirements Met',
          items: [
            'Effective communication throughout the simulation.',
            'Demonstrated professional interaction standards.',
          ],
        },
      ],
    },
    {
      type: 'numeric',
      score: 5,
      scoreMax: 5,
      pass: true,
      title: 'Compliance Standards',
      skillLevel: 'expert',
      content: [
        {
          title: 'Requirements Met',
          items: [
            'Successfully adhered to all regulatory requirements.',
            'Demonstrated compliant communication strategies.',
          ],
        },
      ],
    },
  ],
};

// 4. All Pass with 1 Violation
export const mockPassWithViolationScore: ISimulationScore = {
  ...mockAllPassScore,
  score: 4,
  uuid: 'mock-pass-with-violation',
  simulationId: '248',
  categories: [
    ...mockAllPassScore.categories,
    {
      type: 'violation',
      score: 0,
      scoreMax: 5,
      pass: false,
      title: 'Documentation Standards',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Compliance Violations',
          items: [
            'Failed to properly document required safety information.',
            'Incomplete record keeping during simulation.',
          ],
        },
      ],
    },
  ],
};

// 5. Mix of Pass/Fail
export const mockPassFailMixScore: ISimulationScore = {
  uuid: 'mock-pass-fail-mix',
  type: 'numeric',
  score: 2,
  scoreMax: 5,
  description: 'Mixed performance assessment',
  simulationId: '249',
  dateCompleted: getRandomDatetimeString(),
  certificationId: '1',
  categories: [
    {
      type: 'numeric',
      score: 1,
      scoreMax: 5,
      pass: false,
      title: 'Product Knowledge',
      skillLevel: 'intermediate',
      content: [
        {
          title: 'Requirements Met',
          items: [
            'Demonstrated adequate product knowledge.',
            'Successfully answered technical questions.',
          ],
        },
      ],
    },
    {
      type: 'numeric',
      score: 0,
      scoreMax: 5,
      pass: false,
      title: 'Safety Communication',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Requirements Not Met',
          items: [
            'Failed to adequately communicate safety profile.',
            'Missed critical safety messaging opportunities.',
          ],
        },
      ],
    },
    {
      type: 'numeric',
      score: 1,
      scoreMax: 5,
      pass: false,
      title: 'Professional Conduct',
      skillLevel: 'advanced',
      content: [
        {
          title: 'Requirements Met',
          items: [
            'Maintained professional demeanor throughout.',
            'Demonstrated appropriate interaction skills.',
          ],
        },
      ],
    },
    {
      type: 'numeric',
      score: 0,
      scoreMax: 5,
      pass: false,
      title: 'Objection Handling',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Requirements Not Met',
          items: [
            'Unable to effectively address provider concerns.',
            'Lacks systematic approach to objection handling.',
          ],
        },
      ],
    },
  ],
};

// 6. Mix of Pass/Fail with 1 Violation
export const mockPassFailWithViolationScore: ISimulationScore = {
  ...mockPassFailMixScore,
  uuid: 'mock-pass-fail-with-violation',
  simulationId: '250',
  categories: [
    ...mockPassFailMixScore.categories,
    {
      type: 'violation',
      score: 0,
      scoreMax: 5,
      pass: false,
      title: 'Promotional Compliance',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Compliance Violations',
          items: [
            'Used unapproved promotional claims during presentation.',
            'Failed to provide required fair balance information.',
          ],
        },
      ],
    },
  ],
};

// 7. All Fail
export const mockAllFailScore: ISimulationScore = {
  uuid: 'mock-all-fail',
  type: 'numeric',
  pass: false,
  score: 0,
  scoreMax: 5,
  description: 'Assessment not passed - remedial training required',
  simulationId: '3',
  dateCompleted: getRandomDatetimeString(),
  certificationId: '1',
  categories: [
    {
      type: 'numeric',
      score: 0,
      scoreMax: 5,
      pass: false,
      title: 'Clinical Knowledge',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Requirements Not Met',
          items: [
            'Insufficient understanding of clinical data.',
            'Unable to articulate mechanism of action clearly.',
            'Requires fundamental clinical training.',
          ],
        },
      ],
    },
    {
      type: 'numeric',
      score: 0,
      scoreMax: 5,
      pass: false,
      title: 'Communication Skills',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Requirements Not Met',
          items: [
            'Poor communication throughout simulation.',
            'Failed to engage healthcare provider effectively.',
            'Needs communication skills development.',
          ],
        },
      ],
    },
    {
      type: 'numeric',
      score: 0,
      scoreMax: 5,
      pass: false,
      title: 'Sales Methodology',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Requirements Not Met',
          items: [
            'No evidence of structured sales approach.',
            'Failed to identify customer needs.',
            'Unable to close for commitment.',
          ],
        },
      ],
    },
  ],
};

// 8. All Fail with 1 Violation
export const mockFailWithViolationScore: ISimulationScore = {
  ...mockAllFailScore,
  uuid: 'mock-fail-with-violation',
  simulationId: '252',
  categories: [
    ...mockAllFailScore.categories,
    {
      type: 'violation',
      score: 0,
      scoreMax: 5,
      title: 'Ethical Standards',
      skillLevel: 'awareness',
      content: [
        {
          title: 'Compliance Violations',
          items: [
            'Made inappropriate claims about competitor products.',
            'Violated ethical standards during customer interaction.',
            'Immediate ethics training required.',
          ],
        },
      ],
    },
  ],
};

/**
 * Consolidated mock responses object
 */
export const mockScoreResponses = {
  allFail: mockAllFailScore,
  allNumeric: mockAllNumericScore,
  allNumericLow: mockAllNumericLowScore,
  allPass: mockAllPassScore,
  failWithViolation: mockFailWithViolationScore,
  numericWithViolation: mockNumericWithViolationScore,
  passFailMix: mockPassFailMixScore,
  passFailWithViolation: mockPassFailWithViolationScore,
  passWithViolation: mockPassWithViolationScore,
};

/**
 * Default export - most commonly used mock
 */
export const mockSimulationScore = mockAllNumericScore;

/**
 * Mock response array for testing list scenarios
 */
export const mockSimulationScores: ISimulationScore[] = [
  mockScoreResponses['allNumeric'],
  mockScoreResponses['allNumericLow'],
  mockScoreResponses['numericWithViolation'],
  mockScoreResponses['allPass'],
  mockScoreResponses['passFailMix'],
  mockScoreResponses['allFail'],
  mockScoreResponses['failWithViolation'],
  mockScoreResponses['passWithViolation'],
  mockScoreResponses['passFailWithViolation'],
];

/**
 * Mock API ScoreCards
 * Note: These are structured to align with the expected API response format,
 * including nested local_data and scorecard_json
 */
const mockApiRoleplayScoreCards: ApiScoreCard[] = [
  mockSimulationScores[0],
  mockSimulationScores[1],
  mockSimulationScores[2],
].map((score, index) => {
  const scorecard: ApiScoreCard = {
    id: index + 1,
    session_id: index + 1,
    scenario_id: 1, // Same scenario ID for first few scores
    rubric_version_id: 1,
    card_location: 'local',
    submitted_at: score.dateCompleted?.toString(),
    created_at: getRandomDatetimeString(),
    updated_at: getRandomDatetimeString(),
    is_shared_with_manager: score.sharedWithManager || false,
    local_data: {
      scorecard_json: {
        certificationTitle: `Role-play simulation ${score.simulationId || ''}`,
        score: {
          score: score.score,
          scoreMax: score.scoreMax,
          description: score.description,
          categories: score.categories.map((cat) => ({
            type: cat.type,
            title: cat.title,
            skillLevel: cat.skillLevel,
            score: cat.score,
            scoreMax: cat.scoreMax,
            content: cat.content,
          })),
          dateCompleted: score.dateCompleted?.toString() || '',
          simulationId: score.simulationId || '',
          uuid: score.uuid || '',
        },
      },
    },
  };

  return scorecard;
});

/**
 * Mock API ScoreCard for Certification Practice - using the 'passFailWithViolation'
 * score to represent a practice attempt with mixed results
 */
const mockApiPracticeScoreCard: ApiScoreCard[] = mockSimulationScores
  .filter((sc) => sc.uuid === 'mock-pass-fail-with-violation')
  .map((score) => {
    const scorecard: ApiScoreCard = {
      id: 54321,
      session_id: 15,
      scenario_id: 3,
      rubric_version_id: 1,
      card_location: 'local',
      submitted_at: score.dateCompleted?.toString(),
      created_at: getRandomDatetimeString(),
      updated_at: getRandomDatetimeString(),
      is_shared_with_manager: score.sharedWithManager || false,
      local_data: {
        scorecard_json: {
          certPass: score?.pass || false,
          certificationTitle: 'Practice Certification',
          score: {
            score: score.score,
            scoreMax: score.scoreMax,
            description: score.description,
            categories: score.categories.map((cat) => ({
              type: cat.type,
              title: cat.title,
              pass: cat.pass,
              skillLevel: cat.skillLevel,
              score: cat.score,
              scoreMax: cat.scoreMax,
              content: cat.content,
            })),
            dateCompleted: score.dateCompleted?.toString() || '',
            simulationId: score.simulationId || '',
            uuid: score.uuid || '',
          },
        },
      },
    };

    return scorecard;
  });

/**
 * Mock API ScoreCards for Certification - using the 'allPass' score to
 * represent a successful certification attempt
 */
const mockApiCertificationScoreCard: ApiScoreCard[] = mockSimulationScores
  .filter((sc) => sc.uuid === 'mock-all-pass')
  .map((score) => {
    const scorecard: ApiScoreCard = {
      id: 12345,
      session_id: 12,
      scenario_id: 4,
      rubric_version_id: 1,
      card_location: 'local',
      submitted_at: score.dateCompleted?.toString(),
      created_at: getRandomDatetimeString(),
      updated_at: getRandomDatetimeString(),
      is_shared_with_manager: score.sharedWithManager || false,
      local_data: {
        scorecard_json: {
          certPass: score?.pass || true,
          certificationTitle: 'Final Certification',
          score: {
            score: score.score,
            scoreMax: score.scoreMax,
            description: score.description,
            categories: score.categories.map((cat) => ({
              type: cat.type,
              title: cat.title,
              pass: cat.pass,
              skillLevel: cat.skillLevel,
              score: cat.score,
              scoreMax: cat.scoreMax,
              content: cat.content,
            })),
            dateCompleted: score.dateCompleted?.toString() || '',
            simulationId: score.simulationId || '',
            uuid: score.uuid || '',
          },
        },
      },
    };

    return scorecard;
  });

export const mockApiScoreCards: ApiScoreCard[] = [
  // First simulation gets multiple scores (different scenarios)
  ...mockApiRoleplayScoreCards,

  // Make passFailViolation go to cert practice
  ...mockApiPracticeScoreCard,

  // Make allPass go to final cert
  ...mockApiCertificationScoreCard,
];

export const mockApiScoreCardsV2: ApiScoreCardv2[] = mockV2Response as unknown as ApiScoreCardv2[];